import styles from './TransactionStatus.module.scss';
import { withStyles, Icon, MaterialSymbol, TransactionStatus as VPTransactionStatus, GetColorStatus } from 'vopay';

type TransactionStatusProps = {
  status:
    | 'requested'
    | 'completed'
    | 'failed'
    | 'upcoming'
    | 'processing'
    | 'active'
    | 'not active'
    | 'partially paid'
    // Partially settled, but the latest payment failed: same icon, red
    // (Figma: "Only show status in red if the last transaction status failed").
    | 'partially paid failed'
    | 'open'
    | 'reversed'
    | 'blocked'
    | 'rejected'
    | 'approved'
    | ReturnType<typeof GetColorStatus>
    | `${VPTransactionStatus}`;
  label: string;
};

const classes = withStyles(styles);

export default function TransactionStatus({ status, label }: TransactionStatusProps) {
  const getStatusIcon = () => {
    // Define the icons for each status
    const statusIcons: { [key: string]: React.ReactNode } = {
      requested: <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      neutral: <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      info: <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      pending: <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      failed: <MaterialSymbol icon="cancel" size="18px" color="#e8160b" />,
      'in progress': <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      sent: <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      upcoming: <MaterialSymbol icon="calendar_today" size="18px" />,
      successful: <MaterialSymbol icon="check_circle" size="18px" color="#008107" />,
      complete: <MaterialSymbol icon="check_circle" size="18px" color="#008107" />,
      settled: <MaterialSymbol icon="check_circle" size="18px" color="#008107" />,
      active: <MaterialSymbol icon="check_circle" size="18px" color="#008107" />,
      positive: <MaterialSymbol icon="check_circle" size="18px" color="#008107" />,
      negative: <MaterialSymbol icon="cancel" size="18px" color="#e8160b" />,
      cancelled: <MaterialSymbol icon="cancel" size="18px" color="#e8160b" />,
      blocked: <MaterialSymbol icon="block" size="18px" color="#2D2D2DBF" />,
      'cancellation requested': <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      'pending Approval': (
        <div>
          <Icon icon="transactionPending" height="18px" width="18px" color="none" />
        </div>
      ),
      waiting: <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      'not active': <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      open: <Icon icon="transactionPending" height="18px" width="18px" color="none" />,
      'partially paid': <Icon icon="partiallyPaid" height="18px" width="18px" color="none" />,
      'partially paid failed': <Icon icon="partiallyPaid" height="18px" width="18px" color="error" />,
      reversed: <MaterialSymbol icon="undo" size="18px" color="#e8160b" />,
      rejected: <MaterialSymbol icon="cancel" size="18px" color="#e8160b" />,
      approved: <MaterialSymbol icon="check_circle" size="18px" color="#008107" />,
    };

    // Return the icon based on the status
    return statusIcons[status] || <Icon icon="transactionPending" height="18px" width="18px" color="none" />;
  };

  const getStatusColor = () => {
    // Define the colors for each status
    const statusColors: { [key: string]: string } = {
      requested: 'gray',
      neutral: 'gray',
      info: 'gray',
      pending: 'gray',
      failed: 'red',
      'in progress': 'gray',
      sent: 'gray',
      upcoming: 'blue',
      successful: 'green',
      complete: 'green',
      settled: 'green',
      active: 'green',
      positive: 'green',
      negative: 'red',
      'not active': 'gray',
      cancelled: 'red',
      'cancellation requested': 'gray',
      'pending Approval': 'gray',
      waiting: 'gray',
      'partially paid': 'gray',
      'partially paid failed': 'red',
      open: 'gray',
      reversed: 'red',
      blocked: 'gray',
      rejected: 'red',
      approved: 'green',
    };

    // Return the color based on the status
    return statusColors[status] || 'gray';
  };

  return (
    <div className={classes('transactionStatus', getStatusColor())}>
      {getStatusIcon()}
      <div className={classes('statusLabel')}>{label}</div>
    </div>
  );
}
